import '../../../styles/restScreens.css';
import '../../../styles/eventsCheckin.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabaseClient } from '../../../config/supabase';
import Button3D from '../../ui/Button3D';
import HoverHelp from '../../shared/HoverHelp';
import useModuleAccess from '../../../hooks/useModuleAccess';
import { GUESTS_SCREEN_LABEL } from '../eventLabels';
import { DEFAULT_EVENT_SETTINGS } from '../eventSettings';
import { loadEventSettingsOrDefaults } from '../eventSettingsApi';
import { resolveEventWords } from '../eventWords';
import { loadEventForGuests } from '../guests/guestsApi';
import { GUEST_INPUT_CLASS } from '../guests/guestStyles';
import { DOOR_SCREEN_LABEL, attendanceSummary, scanResultView } from './checkin';
import { friendlyCheckinError, loadAttendance, scanPass } from './checkinApi';
import useQrScanner, { CAMERA_MESSAGES } from './useQrScanner';

const HELP = {
    title: 'Door check-in',
    body: 'Scan each guest’s QR pass with this device’s camera, or type the pass code (a handheld scanner types it for you). A guest is counted once, however often the pass is scanned. To find a guest without a pass, go back to the guest list and check them in there.',
};
const REPEAT_WINDOW_MS = 4000;   // the same pass held in front of the camera is read once, not five times a second

// Module 19 (BL-380) G5 - the door scanner for one event: a staff-only, signed-in screen (Events edit). Every scan goes to ONE checked database
// function (m19_checkin_scan, migration 246), which decides; this screen only shows the answer. Nothing is sent to anyone.
export default function DoorCheckInView({ engagementId, userId, isUser = false, onBack, onEvents, client = supabaseClient, scannerOptions = {} }) {
    const { allowed: eventsEditAllowed } = useModuleAccess(isUser ? 'events' : null, 'edit');
    const canEdit = !isUser || eventsEditAllowed;
    const [event, setEvent] = useState(null);
    const [settings, setSettings] = useState(DEFAULT_EVENT_SETTINGS);
    const [ready, setReady] = useState(false);
    const [numbers, setNumbers] = useState(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);   // { view, token }
    const [error, setError] = useState('');
    const [code, setCode] = useState('');
    const last = useRef({ token: '', at: 0 });
    const busyRef = useRef(false);
    const words = useMemo(() => resolveEventWords(settings), [settings]);

    const refreshNumbers = useCallback(async () => {
        try { setNumbers(await loadAttendance(client, engagementId)); } catch { /* the numbers are a convenience at the door */ }
    }, [client, engagementId]);

    useEffect(() => {
        let alive = true;
        Promise.all([
            loadEventForGuests(client, engagementId).catch(() => null),
            loadEventSettingsOrDefaults(client),
        ]).then(([nextEvent, nextSettings]) => {
            if (!alive) return;
            setEvent(nextEvent); setSettings(nextSettings); setReady(true);
        });
        refreshNumbers();
        return () => { alive = false; };
    }, [client, engagementId, refreshNumbers]);

    const submit = useCallback(async (raw, withPlusOnes = false) => {
        const token = String(raw || '').trim();
        if (!token || busyRef.current) return;
        const now = Date.now();
        if (!withPlusOnes && token === last.current.token && now - last.current.at < REPEAT_WINDOW_MS) return;
        last.current = { token, at: now };
        busyRef.current = true; setBusy(true); setError('');
        try {
            const answer = await scanPass(client, engagementId, token, withPlusOnes);
            setResult({ view: scanResultView(answer), token });
            setCode('');
            if (answer?.ok) refreshNumbers();
        } catch (failure) {
            setResult(null);
            setError(friendlyCheckinError(failure));
        }
        busyRef.current = false; setBusy(false);
    }, [client, engagementId, refreshNumbers]);

    const scanner = useQrScanner({ onCode: submit, ...scannerOptions });
    useEffect(() => () => scanner.stop(), []); // eslint-disable-line react-hooks/exhaustive-deps

    const canceled = event?.engagement?.status === 'canceled';
    const checkinOff = settings.checkin_enabled === false;
    const qrOff = settings.checkin_qr_enabled === false;
    const summary = attendanceSummary(numbers);
    const blocked = !ready ? '' : !event ? `That ${words.eventLower} was not found.`
        : checkinOff ? 'Check-in is turned off in Events Settings.'
            : qrOff ? 'QR check-in is turned off in Events Settings. Check guests in from the guest list.'
                : canceled ? `This ${words.eventLower} is canceled, so nobody can be checked in.`
                    : !canEdit ? `You can view this ${words.eventLower}, but you do not have permission to check guests in.` : '';
    const view = result?.view;

    return (
        <section className="ia-d2 ia-events rounded-2xl p-3 sm:p-5 space-y-4" data-m6-layout="events" data-m6-component="EventDoorCheckIn" aria-label={DOOR_SCREEN_LABEL}>
            <div className="min-w-0">
                <nav aria-label="Breadcrumb" data-testid="door-breadcrumb">
                    <ol className="flex flex-wrap items-center gap-2">
                        <li className="flex items-center gap-2"><Button3D variant="pill" onClick={onEvents}>{words.events}</Button3D><span aria-hidden="true">›</span></li>
                        <li className="flex min-w-0 items-center gap-2"><span className="min-w-0 break-words font-semibold">{event?.event_name || `This ${words.eventLower}`}</span><span aria-hidden="true">›</span></li>
                        <li className="flex items-center gap-2"><Button3D variant="pill" onClick={onBack} data-testid="door-back">{GUESTS_SCREEN_LABEL}</Button3D><span aria-hidden="true">›</span></li>
                    </ol>
                </nav>
                <div className="mt-2 flex items-center gap-2">
                    <h2 className="text-2xl font-bold">{DOOR_SCREEN_LABEL}</h2>
                    <HoverHelp helpId="events.door" content={HELP} userId={userId} />
                </div>
            </div>

            {summary && (
                <div className="ia-d2-card" data-testid="door-counts" aria-live="polite">
                    <div className="text-xs font-bold uppercase tracking-wide">Checked in</div>
                    <div className="text-2xl font-bold leading-tight" data-testid="door-counts-value">{summary.checkedIn}</div>
                    <div className="text-sm">{summary.note}</div>
                </div>
            )}

            {!ready ? <p className="py-6 text-center">Loading…</p> : blocked ? (
                <div className="ia-d2-card space-y-3" data-testid="door-blocked">
                    <p className="font-semibold">{blocked}</p>
                    <Button3D onClick={onBack}>Back to the guest list</Button3D>
                </div>
            ) : (
                <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,560px)_minmax(0,1fr)]">
                    <div className="space-y-3">
                        <div className="ia-door__camera" data-testid="door-camera">
                            <video ref={scanner.videoRef} muted playsInline aria-label="Camera view" hidden={scanner.state !== 'running' && scanner.state !== 'starting'} />
                            {scanner.state === 'running' && <div className="ia-door__aim" aria-hidden="true" />}
                            {scanner.state !== 'running' && <div className="ia-door__idle">{CAMERA_MESSAGES[scanner.state] || CAMERA_MESSAGES.idle}</div>}
                        </div>
                        <p className="text-sm" data-testid="door-camera-status" role="status">{CAMERA_MESSAGES[scanner.state] || CAMERA_MESSAGES.idle}</p>
                        <div className="flex flex-wrap gap-2">
                            {scanner.state === 'running' || scanner.state === 'starting'
                                ? <Button3D onClick={scanner.stop} data-testid="door-camera-stop">Stop camera</Button3D>
                                : <Button3D variant="go" onClick={scanner.start} data-testid="door-camera-start">{scanner.state === 'idle' ? 'Start camera' : 'Try the camera again'}</Button3D>}
                        </div>
                        <form className="space-y-2" onSubmit={(e) => { e.preventDefault(); last.current = { token: '', at: 0 }; submit(code); }} data-testid="door-code-form">
                            <label htmlFor="door-code" className="block text-sm font-bold">Pass code</label>
                            <div className="flex flex-wrap gap-2">
                                <input id="door-code" className={`${GUEST_INPUT_CLASS} min-w-0 flex-1`} value={code} onChange={(e) => setCode(e.target.value)} placeholder="A handheld scanner types it here" autoComplete="off" autoCapitalize="off" spellCheck={false} />
                                <Button3D type="submit" variant="go" disabled={busy || !code.trim()}>Check in</Button3D>
                            </div>
                        </form>
                    </div>

                    <div className="space-y-3" aria-live="assertive" data-testid="door-result-area">
                        {busy && <p role="status">Checking…</p>}
                        {error && <p role="alert" className="ia-resv-error">{error}</p>}
                        {view && (
                            <div className="ia-d2-card ia-door__result space-y-2" data-tone={view.tone} data-testid="door-result">
                                <div className="ia-door__result-title">{view.tone === 'in' ? '✓ ' : view.tone === 'repeat' ? '↺ ' : '✕ '}{view.title}</div>
                                {view.name && <div className="ia-door__result-name" data-testid="door-result-name">{view.name}</div>}
                                {view.detail && <div className="text-sm" data-testid="door-result-detail">{view.detail}</div>}
                                {view.plusOnesLeft > 0 && (
                                    <Button3D variant="go" onClick={() => submit(result.token, true)} disabled={busy} data-testid="door-party">
                                        {`Check in ${view.plusOnesLeft} ${view.plusOnesLeft === 1 ? 'plus-one' : 'plus-ones'} too`}
                                    </Button3D>
                                )}
                            </div>
                        )}
                        {!view && !error && !busy && <div className="ia-d2-card text-sm" data-testid="door-waiting">Ready for the next guest.</div>}
                        <div className="flex flex-wrap items-center gap-2 text-sm"><span>No pass?</span><Button3D variant="pill" onClick={onBack} data-testid="door-find-guest">Find the guest on the list</Button3D></div>
                    </div>
                </div>
            )}
        </section>
    );
}
